import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import Icon from '~/app/common/Icon';
import useTranslation from '~/app/hooks/useTranslation';
import { CommunicationRole } from '~/types';
import classes from './style.module.scss';

export interface RoleListItemProps {
    role: CommunicationRole
    onSelect?: (role: CommunicationRole) => void
}

export type RoleListItemComponent = React.FC<RoleListItemProps>;

const RoleListItem: RoleListItemComponent = ({ role, onSelect }) => {
    const { t } = useTranslation('communication');
    const count = role.userCount ?? 0;
    const isConfigured = count > 0;
    const icon = count > 1 ? 'people' : 'person';

    const subtitle = useMemo(() => {
        if (!isConfigured) {
            return t('noPersonConfigured');
        }

        const countLabel = count === 1
            ? t('personSingular', { count })
            : t('personPlural', { count });

        if (role.primaryName) {
            return count > 1
                ? `${role.primaryName} \u00b7 ${countLabel}`
                : role.primaryName;
        }

        return countLabel;
    }, [isConfigured, count, role.primaryName, t]);

    const clickHandler = useCallback(() => {
        if (!isConfigured) {
            return;
        }
        onSelect?.(role);
    }, [isConfigured, onSelect, role]);

    return (
        <div
            className={classNames(classes['role-item'], !isConfigured && classes['disabled'])}
            onClick={clickHandler}
        >
            <div className={classes['avatar']}>
                <Icon name={isConfigured ? icon : 'error_outline'} />
            </div>
            <div className={classes['info']}>
                <div className={classes['title']}>{role.name}</div>
                <div className={classes['subtitle']}>{subtitle}</div>
            </div>
            {isConfigured && (
                <div className={classes['right-icon']}>
                    <Icon name="keyboard_arrow_right" />
                </div>
            )}
        </div>
    );
};

export default RoleListItem;
